import React, { useEffect, useRef, useState } from "react";
import type { SiteId } from "@siteagain/shared-types";

import type { Locale } from "../i18n";
import type { OperatorCopy } from "./copy";
import type { MC2Copy } from "./v2/copy";
import { operatorPost } from "./format";

type OnboardGoal = "leads" | "traffic" | "maintenance";

const GOALS: readonly OnboardGoal[] = ["leads", "traffic", "maintenance"];

/** "acme.com", "www.acme.com/" or "https://acme.com" all mean the same site. Null when it is not an address. */
export function normalizeSiteUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    const parsed = new URL(/^https?:\/\//iu.test(trimmed) ? trimmed : `https://${trimmed}`);
    if (!parsed.hostname.includes(".") || parsed.hostname.endsWith(".")) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

function nameFromUrl(url: string | null): string {
  if (!url) return "";
  return new URL(url).hostname.replace(/^www\./u, "");
}

/**
 * One form, no wizard. The address is the only required field; the name follows it,
 * the goal is one tap, and the rest sits behind More options. The footer says exactly
 * what will happen next, so pressing the button is not a leap of faith.
 */
export function OnboardSiteModal({
  copy,
  ops,
  locale,
  onClose,
  onCreated,
}: {
  copy: MC2Copy;
  ops: OperatorCopy;
  locale: Locale;
  onClose(): void;
  onCreated(siteId: SiteId): void;
}) {
  const t = copy.add;
  const [url, setUrl] = useState("");
  const [customName, setCustomName] = useState<string | null>(null);
  const [goal, setGoal] = useState<OnboardGoal>("leads");
  const [siteLocale, setSiteLocale] = useState<"en" | "fr" | "es">(locale === "fr" ? "fr" : "en");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const urlRef = useRef<HTMLInputElement>(null);

  const normalized = normalizeSiteUrl(url);
  const name = customName ?? nameFromUrl(normalized);
  const urlInvalid = touched && url.trim() !== "" && normalized === null;
  const goalLabel = ops.fleet.onboardModal.goals[goal];

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    urlRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        const form = document.querySelector<HTMLFormElement>("#mc2-add-form");
        form?.requestSubmit();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previous?.focus();
    };
  }, [onClose]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (!normalized || !name.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const response = await operatorPost<{ site: { id: SiteId } }>("/operator/sites", {
        name: name.trim(),
        url: normalized,
        goal,
        locale: siteLocale,
        ...(ownerEmail.trim() ? { ownerEmail: ownerEmail.trim() } : {}),
      });
      onCreated(response.site.id);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : (locale === "fr" ? "Impossible d'ajouter le site." : "Could not add the website."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="mc2-scrim"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="mc2-scriminner">
        <div className="mc2-dlg" role="dialog" aria-modal="true" aria-labelledby="mc2-add-title">
          <form id="mc2-add-form" onSubmit={(event) => void submit(event)} noValidate>
            <div className="sec" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div>
                <div className="mc2-eyebrow">{t.eyebrow}</div>
                <h1 id="mc2-add-title">{t.title}</h1>
              </div>
              <button type="button" className="mc2-btn ghost sm" onClick={onClose} aria-label={t.close}>Esc</button>
            </div>

            {error && <div className="sec"><p className="mc2-banner bad" role="alert" style={{ margin: 0 }}>{error}</p></div>}

            <div className="sec">
              <div className="mc2-lbl"><span className="mc2-num">1</span>{t.step1}</div>
              <label className="mc2-url">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="var(--mc-muted)" strokeWidth="1.5" aria-hidden="true">
                  <circle cx="9" cy="9" r="7" /><path d="M2 9h14M9 2c2 2.2 2 11.8 0 14M9 2c-2 2.2-2 11.8 0 14" />
                </svg>
                <input
                  ref={urlRef}
                  type="text"
                  inputMode="url"
                  autoComplete="url"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                  value={url}
                  aria-invalid={urlInvalid}
                  aria-describedby={urlInvalid ? "mc2-add-url-error" : undefined}
                  onChange={(event) => setUrl(event.target.value)}
                  onBlur={() => setTouched(true)}
                  placeholder={ops.fleet.onboardModal.urlPlaceholder}
                />
                {normalized && <span className="mc2-chip info">{new URL(normalized).hostname}</span>}
              </label>
              {urlInvalid && <small id="mc2-add-url-error" style={{ color: "var(--mc-error)", display: "block", marginTop: 8 }}>{t.urlInvalid}</small>}
              <div className="mc2-muted" style={{ fontSize: 13, marginTop: 8 }}>{t.urlHint}</div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12, marginTop: 16 }}>
                <div>
                  <label className="mc2-eyebrow" htmlFor="mc2-add-name" style={{ display: "block", marginBottom: 6 }}>{t.nameLabel}</label>
                  <input
                    id="mc2-add-name"
                    type="text"
                    required
                    maxLength={160}
                    value={name}
                    onChange={(event) => setCustomName(event.target.value)}
                    placeholder={ops.fleet.onboardModal.namePlaceholder}
                    style={{ width: "100%", height: 42, border: "1px solid var(--line)", borderRadius: 10, padding: "0 12px", font: "400 14px var(--font-sans)", background: "#fff" }}
                  />
                  {customName === null && <div className="mc2-muted" style={{ fontSize: 12, marginTop: 6 }}>{t.nameHint}</div>}
                </div>
                <div>
                  <label className="mc2-eyebrow" htmlFor="mc2-add-owner" style={{ display: "block", marginBottom: 6 }}>{t.ownerLabel}</label>
                  <input
                    id="mc2-add-owner"
                    type="email"
                    autoComplete="off"
                    value={ownerEmail}
                    onChange={(event) => setOwnerEmail(event.target.value)}
                    placeholder={t.ownerPlaceholder}
                    style={{ width: "100%", height: 42, border: "1px solid var(--line)", borderRadius: 10, padding: "0 12px", font: "400 14px var(--font-sans)", background: "#fff" }}
                  />
                </div>
              </div>
            </div>

            <div className="sec">
              <div className="mc2-lbl"><span className="mc2-num">2</span>{t.step2}</div>
              <div className="mc2-opts" role="radiogroup" aria-label={t.step2}>
                {GOALS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={goal === value}
                    className={`mc2-opt${goal === value ? " on" : ""}`}
                    onClick={() => setGoal(value)}
                  >
                    <span className="t">{ops.fleet.onboardModal.goals[value]}<span className="mc2-radio" /></span>
                    <span className="mc2-muted">{ops.fleet.onboardModal.goalHints[value]}</span>
                  </button>
                ))}
              </div>

              <details style={{ marginTop: 14 }}>
                <summary style={{ cursor: "pointer", fontSize: 13, color: "var(--mc-muted)" }}>{ops.fleet.onboardModal.moreOptions}</summary>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 10 }}>
                  <label className="mc2-eyebrow" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {t.languageLabel}
                    <select
                      value={siteLocale}
                      onChange={(event) => setSiteLocale(event.target.value as "en" | "fr" | "es")}
                      style={{ height: 34, border: "1px solid var(--line)", borderRadius: 8, background: "#fff", padding: "0 8px", font: "400 13px var(--font-sans)" }}
                    >
                      <option value="en">English</option>
                      <option value="fr">Français</option>
                      <option value="es">Español</option>
                    </select>
                  </label>
                </div>
              </details>
            </div>

            <div className="sec">
              <div className="mc2-lbl"><span className="mc2-num">3</span>{t.step3}</div>
              {t.next.map((line, index) => (
                <div className="mc2-check" key={line}>
                  <span className={`mc2-ic ${index === 0 && normalized ? "ok" : "todo"}`}>{index === 0 && normalized ? "✓" : index === 0 ? "" : "!"}</span>
                  <span>{line}</span>
                  <span className="mc2-mono mc2-muted" style={{ fontSize: 12 }}>{index === 0 ? (normalized ? t.statusReady : t.statusWaiting) : index === 1 ? t.statusInbox : t.statusApproval}</span>
                </div>
              ))}
            </div>

            <div className="sec">
              <div className="mc2-lbl"><span className="mc2-num">4</span>{t.step4}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
                <div style={{ border: "1px solid var(--line)", borderRadius: 12, padding: 14, background: "#fff" }}>
                  <div className="mc2-eyebrow">{t.auditName}</div>
                  <div style={{ fontWeight: 600, fontSize: 16, marginTop: 6 }}>{goalLabel}</div>
                  <div className="mc2-muted" style={{ fontSize: 13 }}>{t.auditDesc}</div>
                </div>
                <div style={{ border: "1px solid var(--line)", borderRadius: 12, padding: 14, background: "#fff", display: "flex", gap: 12, justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div className="mc2-eyebrow">{t.mailName}</div>
                    <div style={{ fontWeight: 600, fontSize: 16, marginTop: 6 }}>{goalLabel}</div>
                    <div className="mc2-muted" style={{ fontSize: 13 }}>{t.mailDesc}</div>
                  </div>
                  <span className="mc2-chip line">{t.statusPlan}</span>
                </div>
              </div>
            </div>

            <div className="mc2-dlgfoot">
              <p>{normalized ? t.summary(name, goalLabel) : (locale === "fr" ? "Collez une adresse ci-dessus. L'audit complet démarre dès l'ajout." : "Paste an address above. The full audit starts as soon as it is added.")}</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" className="mc2-btn ghost" disabled={submitting} onClick={onClose}>{t.cancel}</button>
                <button type="submit" className="mc2-btn primary" disabled={submitting || !normalized || !name.trim()}>
                  {submitting ? ops.action.working : t.cta} <span className="mc2-kbd">⌘↵</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
